import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';

import type { HeroSlide } from '@/features/marketing/banners';
import { cn } from '@/lib/cn';

/** How long a slide holds before the rotation moves on. */
const ADVANCE_MS = 5500;

/** Pointer travel, in px, that reads as a swipe rather than a tap on a slide. */
const SWIPE_PX = 40;

/**
 * The rotating hero on the marketing home page.
 *
 * Each banner is finished artwork carrying its own headline, so the slide is
 * the whole picture and a click follows the words already in it - there is no
 * caption or button drawn on top. The rotation stops whenever someone is
 * looking at it deliberately (hover, keyboard focus, a hidden tab) or has asked
 * for less motion, and only the visible slide is reachable by keyboard.
 */
export function HeroBannerSlider({
  slides,
  className,
  label = 'NeonCarts highlights',
}: {
  slides: HeroSlide[];
  className?: string;
  /** Names the carousel for screen readers. */
  label?: string;
}) {
  const count = slides.length;
  const [current, setCurrent] = useState(0);
  const [held, setHeld] = useState(false);
  const [tabHidden, setTabHidden] = useState(false);
  const [animate, setAnimate] = useState(true);

  // Only the slide on screen and the one after it are in the DOM as images: all
  // thirteen banners stacked in the viewport would otherwise download at once,
  // since an image that is merely transparent still counts as visible.
  const [mounted, setMounted] = useState<number[]>(() => (count > 1 ? [0, 1] : [0]));

  const go = useCallback(
    (index: number) => setCurrent(((index % count) + count) % count),
    [count],
  );

  /** Start a slide downloading before it is asked for. Mounted slides are never
   *  dropped, so a banner is fetched at most once. */
  const preload = useCallback(
    (index: number) =>
      setMounted((prev) => (prev.includes(index) ? prev : [...prev, index])),
    [],
  );

  useEffect(() => {
    const next = (current + 1) % count;
    setMounted((prev) =>
      prev.includes(current) && prev.includes(next)
        ? prev
        : [...new Set([...prev, current, next])],
    );
  }, [current, count]);

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setAnimate(!query.matches);
    sync();
    query.addEventListener('change', sync);
    return () => query.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    const sync = () => setTabHidden(document.hidden);
    document.addEventListener('visibilitychange', sync);
    return () => document.removeEventListener('visibilitychange', sync);
  }, []);

  const rotating = animate && !held && !tabHidden && count > 1;

  // A timeout keyed on the current slide rather than a running interval, so
  // picking a slide from the rail gives it a full turn instead of whatever was
  // left of the last one.
  useEffect(() => {
    if (!rotating) return;
    const timer = window.setTimeout(() => setCurrent((index) => (index + 1) % count), ADVANCE_MS);
    return () => window.clearTimeout(timer);
  }, [rotating, current, count]);

  // A swipe ends in a click on the link underneath, so the drag is remembered
  // long enough to swallow that click.
  const pressedAt = useRef<number | null>(null);
  const swiped = useRef(false);

  if (count === 0) return null;

  return (
    <section
      className={cn('group/slider', className)}
      aria-roledescription="carousel"
      aria-label={label}
      onPointerEnter={() => setHeld(true)}
      onPointerLeave={() => setHeld(false)}
      onFocus={() => setHeld(true)}
      onBlur={() => setHeld(false)}
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft') go(current - 1);
        if (event.key === 'ArrowRight') go(current + 1);
      }}
    >
      {/* A phone gets its own artwork: the 3:1 desktop banner is a ~115px strip
          there whose type is too small to read. The phone banners in
          `public/banners/mobile` are drawn at 10:7, so the box takes that
          ratio below `sm` and the desktop one from `sm` up. */}
      <div
        className="relative isolate aspect-[10/7] w-full overflow-hidden rounded-2xl bg-surface shadow-lift sm:aspect-[3/1]"
        onPointerDown={(event) => {
          pressedAt.current = event.clientX;
          swiped.current = false;
        }}
        onPointerUp={(event) => {
          const from = pressedAt.current;
          pressedAt.current = null;
          if (from === null) return;
          const travelled = event.clientX - from;
          if (Math.abs(travelled) < SWIPE_PX) return;
          swiped.current = true;
          go(current + (travelled < 0 ? 1 : -1));
        }}
        onClickCapture={(event) => {
          if (!swiped.current) return;
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        {slides.map((slide, index) => {
          const showing = index === current;

          return (
            <div
              key={slide.src}
              role="group"
              aria-roledescription="slide"
              aria-label={`Slide ${index + 1} of ${count}`}
              aria-hidden={!showing}
              className={cn(
                'absolute inset-0 transition-opacity duration-700 ease-out motion-reduce:transition-none',
                showing ? 'opacity-100' : 'pointer-events-none opacity-0',
              )}
            >
              <Link to={slide.href} tabIndex={showing ? undefined : -1} className="block size-full">
                {mounted.includes(index) ? (
                  // `<source>` rather than CSS, so a phone downloads only the
                  // phone banner and a laptop only the wide one. The media
                  // query is Tailwind's `sm` breakpoint, matching the box.
                  <picture className="block size-full">
                    <source media="(max-width: 639.98px)" srcSet={slide.mobileSrc} width={1500} height={1049} />
                    <img
                      src={slide.src}
                      alt={slide.alt}
                      width={2172}
                      height={724}
                      className="size-full object-cover"
                      decoding="async"
                      loading={index === 0 ? 'eager' : 'lazy'}
                      fetchPriority={index === 0 ? 'high' : 'auto'}
                      draggable={false}
                    />
                  </picture>
                ) : null}
              </Link>
            </div>
          );
        })}

        {count > 1 ? (
          <>
            <SliderArrow side="left" onClick={() => go(current - 1)} />
            <SliderArrow side="right" onClick={() => go(current + 1)} />

            {/* How long the slide has left. It empties when the rotation stops,
                which is the only cue that hovering has paused anything. */}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[3px] bg-ink/10" aria-hidden>
              {rotating ? (
                <div
                  key={current}
                  className="h-full origin-left bg-brand-500 animate-[hero-slide-progress_5500ms_linear_forwards]"
                />
              ) : null}
            </div>
          </>
        ) : null}
      </div>

      {count > 1 ? (
        <div className="mt-3 flex flex-wrap items-center justify-center gap-1.5">
          {slides.map((slide, index) => (
            <button
              key={slide.src}
              type="button"
              onClick={() => go(index)}
              // Reaching for a dot is enough warning to fetch the banner behind
              // it, so the jump lands on the picture rather than on a gap.
              onPointerEnter={() => preload(index)}
              onFocus={() => preload(index)}
              aria-label={`Show slide ${index + 1}: ${slide.alt}`}
              aria-current={index === current}
              className={cn(
                'h-1.5 rounded-full transition-all duration-300',
                index === current
                  ? 'w-7 bg-brand-500'
                  : 'w-3 bg-ink/15 hover:w-5 hover:bg-brand-300',
              )}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}

function SliderArrow({ side, onClick }: { side: 'left' | 'right'; onClick: () => void }) {
  const Icon = side === 'left' ? ChevronLeft : ChevronRight;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={side === 'left' ? 'Previous slide' : 'Next slide'}
      className={cn(
        // Out of the way until asked for: the artwork is the point, and on a
        // phone the swipe is the control anyway. Dark rather than white,
        // because every banner is light and a pale control vanishes into it.
        'absolute top-1/2 hidden size-9 -translate-y-1/2 place-items-center rounded-full',
        'bg-ink/55 text-white ring-1 ring-white/25 backdrop-blur-sm',
        'opacity-0 transition hover:bg-ink/75 focus-visible:opacity-100 group-hover/slider:opacity-100',
        'sm:grid',
        side === 'left' ? 'left-3' : 'right-3',
      )}
    >
      <Icon className="size-5" aria-hidden />
    </button>
  );
}
